import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useJWTAuth } from './auth/JWTAuthContext';
import { useAccountLinking } from './auth/useAccountLinking';
import WalletConnectionModal from './components/WalletConnectionModal';
import UsernamePrompt from './components/UsernamePrompt';

const Landing = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isAuthenticated } = useJWTAuth();
  const { user: accountUser } = useAccountLinking();
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [pendingUsername, setPendingUsername] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for auth parameter and auto-open modal
  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (authParam === 'login' && !isAuthenticated() && !accountUser) {
      console.log('🔗 [Landing] Auth parameter detected, opening wallet modal');
      setShowWalletModal(true);
      // Clean up the URL parameter
      setSearchParams({});
    }
  }, [searchParams, isAuthenticated, accountUser, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/news-updates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source: 'homepage'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit email');
      }

      const data = await response.json();
      console.log('Email submitted successfully:', data);
      setSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Error submitting email:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameSubmit = (username) => {
    console.log('👤 [Landing] Username chosen:', username);
    setPendingUsername(username);
    setShowUsernamePrompt(false);
    setShowWalletModal(true);
  };

  const handleUsernamePromptClose = () => {
    setShowUsernamePrompt(false);
    setPendingUsername(null);
  };

  const handleWalletModalClose = () => {
    setShowWalletModal(false);
    setPendingUsername(null);
  };

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: `url('/assets/menus/SiteBackground.jpg')`
    }}>
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            {/* Logo/Title */}
            <div className="mb-6 flex justify-center">
              <img 
                src="/soulwars-logo.png" 
                alt="SoulWars" 
                className="h-24 md:h-32 lg:h-40 w-auto drop-shadow-lg"
              />
            </div>

            {/* Tagline */}
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Epic live battles where strategy meets brute force.
              Command your heroes in the ultimate live social gaming experience, where once they set on their journey, they never stop.
            </p>

            {/* YouTube Video Embed */}
            <div className="mb-12 max-w-4xl mx-auto">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src="https://www.youtube.com/embed/NX-hUvl-WLY"
                    title="SoulWars Gameplay"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
        <div className="mb-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
          {/* Signup/Login Button */}
          {!isAuthenticated() && !accountUser ? (
            <button
              onClick={() => setShowWalletModal(true)}
              className="inline-block bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              🚀 Sign Up / Login
            </button>
          ) : (
            <Link
              to="/heroes"
              className="inline-block bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              🎮 My Heroes
            </Link>
          )}
              
              {/* Enter the Arena Button *
              <a
                href="https://pump.fun/coin/HXik3MQbaoKdFEdbirN8xS5PmjyaQQz66TgZ9Ygvpump"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                💰 Enter the Arena
              </a>
              */}

              {/* Docs & Roadmap Button */}
              <a
                href="https://docs.soulwars.gg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                📚 Docs & Roadmap
              </a>
            </div>

            {/* Play Now! Section */}
            <div className="mb-12 max-w-4xl mx-auto">
              <div className="bg-black bg-opacity-40 backdrop-blur-sm rounded-lg p-8 border border-white border-opacity-20">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">
                  Play Now!
                </h2>
                <p className="text-lg md:text-xl text-gray-100 mb-6 max-w-3xl mx-auto text-center leading-relaxed">
                  SoulWars is played with other players live on streams. You play, gain XP, earn and spend loot, level up, and affect the game all through chat!
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="https://www.youtube.com/@SoulWarsGG"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#DC2626',
                      color: 'white',
                      fontWeight: 600,
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      transform: 'scale(1)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#B91C1C';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#DC2626';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <img 
                      src="/assets/platform/platform-youtube.png" 
                      alt="YouTube" 
                      style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
                    />
                    <span>YouTube</span>
                  </a>
                  <a
                    href="https://www.twitch.tv/soulwarsgg"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#9146FF',
                      color: 'white',
                      fontWeight: 600,
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      transform: 'scale(1)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#772CE8';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#9146FF';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <img 
                      src="/assets/platform/platform-twitch.png" 
                      alt="Twitch" 
                      style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
                    />
                    <span>Twitch</span>
                  </a>
                  <a
                    href="https://x.com/SoulWarsGG"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#000000',
                      color: 'white',
                      fontWeight: 600,
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      transform: 'scale(1)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1F2937';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#000000';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <img 
                      src="/assets/platform/platform-x.png" 
                      alt="X" 
                      style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
                    />
                    <span>X</span>
                  </a>
                  <a
                    href="https://www.tiktok.com/@soulwarsgg?lang=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#000000',
                      color: 'white',
                      fontWeight: 600,
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      transform: 'scale(1)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1F2937';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#000000';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <img 
                      src="/assets/platform/platform-tiktok.png" 
                      alt="TikTok" 
                      style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
                    />
                    <span>TikTok</span>
                  </a>
                  <a
                    href="https://pump.fun/coin/HXik3MQbaoKdFEdbirN8xS5PmjyaQQz66TgZ9Ygvpump"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#1F2937',
                      color: 'white',
                      fontWeight: 600,
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      transform: 'scale(1)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#374151';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#1F2937';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <img 
                      src="/assets/platform/platform-pumpfun.png" 
                      alt="PumpFun" 
                      style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
                    />
                    <span>PumpFun</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-4">
              <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
                <div className="text-3xl mb-4">⚔️</div>
                <h3 className="text-xl font-semibold text-white mb-2">Strategic Combat</h3>
                <p className="text-gray-300">Command armies with tactical precision and magical prowess</p>
              </div>

              <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
                <div className="text-3xl mb-4">🏆</div>
                <h3 className="text-xl font-semibold text-white mb-2">Rewards</h3>
                <p className="text-gray-300">Earn tokens and exclusive rewards for your victories</p>
              </div>

              <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
                <div className="text-3xl mb-4">🧙‍♂️</div>
                <h3 className="text-xl font-semibold text-white mb-2">Magical Classes</h3>
                <p className="text-gray-300">Choose from warriors, mages, and other fantasy archetypes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Power moves for Streamers! Section */}
      <div className="bg-white bg-opacity-10 py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-lg p-8 border border-white border-opacity-20">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">
              Power moves for Streamers!
            </h2>
            <p className="text-lg md:text-xl text-gray-100 mb-6 max-w-3xl mx-auto text-center leading-relaxed">
              Players join from every platform you stream on, and all play together. Drop them rewards and loot, earn revenue when they make purchases!
            </p>

          </div>
        </div>
      </div>

      {/* Join the Community Section */}
      <div className="bg-black bg-opacity-30 py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-8 border border-white border-opacity-20">
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">
              Join the Community
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://x.com/SoulWarsGG"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#000000',
                  color: 'white',
                  fontWeight: 600,
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1F2937';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#000000';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <img 
                  src="/assets/platform/platform-x.png" 
                  alt="X" 
                  style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
                />
                <span>X (@soulwarsgg)</span>
              </a>
              <a
                href="https://t.me/SoulWarz"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#0088cc',
                  color: 'white',
                  fontWeight: 600,
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#006ba3';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#0088cc';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span style={{ fontSize: '20px' }}>@SoulWarz</span>
                <span>Telegram</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stay in Touch Section */}
      <div className="bg-black bg-opacity-30 py-16">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-8 border border-white border-opacity-20">
            <h2 className="text-2xl font-bold text-white text-center mb-4">
              Stay in Touch
            </h2>
            <p className="text-gray-300 text-center mb-6">
              Be the first to know when new features are released.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 disabled:cursor-not-allowed"
                >
                  {loading ? 'Joining...' : 'Join Waiting List'}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <div className="text-green-400 text-4xl mb-4">✓</div>
                <p className="text-white font-semibold">Thank you for joining!</p>
                <p className="text-gray-300 text-sm mt-2">We'll notify you when new features are available.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black bg-opacity-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-400 text-sm">
            <p>&copy; 2025 SoulSeed. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Username Prompt Modal */}
      <UsernamePrompt
        isOpen={showUsernamePrompt}
        onClose={handleUsernamePromptClose}
        onSubmit={handleUsernameSubmit}
        walletAddress={null}
      />
      
      {/* Wallet Connection Modal */}
      <WalletConnectionModal
        isOpen={showWalletModal}
        onClose={handleWalletModalClose}
        pendingUsername={pendingUsername}
      />
    </div>
  );
};

export default Landing;
